import type { Asset } from '../asset';

// Banners disable the download action once the selection holds more than
// this many assets (src/react/shared/logicalComponents/Banners/BatchDownload).
export const DOWNLOAD_SELECTION_LIMIT = 100;

// Batch download is a selection of assets, not a mode — "in batch mode" is
// derived elsewhere as `!selection.isEmpty()`. The selection holds whole
// Asset snapshots rather than ids: a visitor selects, pages on (the asset
// list scrolls out of the current result set), and can still download, and
// the acquisition modal needs the full asset payload, not just an id it
// would have to re-fetch.
export type DownloadSelection = Readonly<{
  assets: readonly Asset[];
  size: number;
  isEmpty(): boolean;
  includes(assetId: string): boolean;
  exceedsLimit(): boolean;
  toggle(asset: Asset): DownloadSelection;
  add(assets: readonly Asset[]): DownloadSelection;
  cleared(): DownloadSelection;
}>;

// Builds a selection from an ordered asset list. Internal — callers go
// through DownloadSelectionFactory, which owns the empty-selection entry
// point. Order: most recently selected first — the acquisition modal treats the
// first asset as representative of the batch.
const build = (assets: readonly Asset[]): DownloadSelection => {
  // Deduplicated by id, keeping the first (most recent) occurrence. The Map
  // also acts as the defensive copy of the caller-owned array.
  const byId = new Map<string, Asset>();
  assets.forEach((asset) => {
    if (!byId.has(asset.id)) byId.set(asset.id, asset);
  });
  const items = [...byId.values()];

  const includes = (assetId: string): boolean => byId.has(assetId);

  return {
    assets: items,
    size: items.length,
    isEmpty: () => items.length === 0,
    includes,
    exceedsLimit: () => items.length > DOWNLOAD_SELECTION_LIMIT,
    toggle: (toggled: Asset): DownloadSelection =>
      includes(toggled.id)
        ? build(items.filter((asset) => asset.id !== toggled.id))
        : build([toggled, ...items]),
    // Purely additive: assets already selected are neither duplicated nor
    // removed. This is what "select all on page" needs.
    add: (additional: readonly Asset[]): DownloadSelection =>
      build([...additional.filter((asset) => !includes(asset.id)), ...items]),
    cleared: () => build([]),
  };
};

export { build as buildDownloadSelection };
